import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, eur, up, gr } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { DateField } from "@/components/DateField";

const SERVICES = [
  "ΑΝΕΛΚΥΣΗ ΣΚΑΦΟΥΣ ΕΩΣ 7Μ",
  "ΑΝΕΛΚΥΣΗ 7-10Μ",
  "ΑΝΕΛΚΥΣΗ ΑΝΩ ΤΩΝ 10Μ",
  "ΚΑΘΕΛΚΥΣΗ",
  "ΠΛΥΣΙΜΟ ΓΑΣΤΡΑΣ",
  "ΑΝΤΙΡΡΥΠΑΝΤΙΚΗ ΒΑΦΗ",
  "ΓΥΑΛΙΣΜΑ ΣΚΑΦΟΥΣ",
  "ΑΛΛΑΓΗ ΛΑΔΙΩΝ ΕΞΩΛΕΜΒΙΑΣ",
  "SERVICE ΕΞΩΛΕΜΒΙΑΣ",
  "SERVICE ΕΣΩΛΕΜΒΙΑΣ",
  "ΦΟΡΤΙΣΗ ΜΠΑΤΑΡΙΑΣ",
  "ΦΥΛΑΞΗ ΤΡΕΙΛΕΡ",
  "ΧΡΗΣΗ ΝΕΡΟΥ",
  "ΧΡΗΣΗ ΗΛΕΚΤΡΙΚΟΥ ΡΕΥΜΑΤΟΣ",
  "ΚΑΛΥΨΗ ΣΚΑΦΟΥΣ ΜΕ ΜΟΥΣΑΜΑ",
  "ΜΕΤΑΦΟΡΑ ΣΚΑΦΟΥΣ ΕΝΤΟΣ ΕΓΚΑΤΑΣΤΑΣΗΣ",
];

export default function BoatDetailsDialog({ boat, open, onOpenChange }) {
  const qc = useQueryClient();
  const [pay, setPay] = useState({ amount: "", note: "", date: "" });
  const [charge, setCharge] = useState({ description: "", amount: "" });
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/config").then((r) => {
      setServices((r.data?.services || []).filter((s) => s.enabled !== false));
    }).catch(() => {});
  }, []);

  if (!boat) return null;

  const refresh = () => qc.invalidateQueries();

  const addPayment = async () => {
    if (!pay.amount || Number(pay.amount) <= 0) return toast.error("Δώστε έγκυρο ποσό");
    await api.post(`/boats/${boat.id}/payments`, { amount: Number(pay.amount), note: pay.note, date: pay.date || undefined });
    setPay({ amount: "", note: "", date: "" });
    await refresh();
    toast.success("Καταχωρήθηκε πληρωμή");
  };
  const delPayment = async (pid) => {
    await api.delete(`/boats/${boat.id}/payments/${pid}`);
    await refresh();
  };
  const addCharge = async () => {
    if (!charge.description.trim() || !charge.amount) return toast.error("Συμπληρώστε περιγραφή & ποσό");
    await api.post(`/boats/${boat.id}/charges`, { description: charge.description.trim(), amount: Number(charge.amount) });
    setCharge({ description: "", amount: "" });
    await refresh();
    toast.success("Προστέθηκε χρέωση");
  };
  const delCharge = async (cid) => {
    await api.delete(`/boats/${boat.id}/charges/${cid}`);
    await refresh();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="boat-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{boat.name} — Οικονομικά</DialogTitle>
          <DialogDescription>Ιδιοκτήτης: {boat.owner} · Θέση #{boat.slot_number}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 rounded-sm border border-border p-3 text-center">
          <div>
            <div className="text-xs text-muted-foreground">Χρέωση έως σήμερα</div>
            <div className="font-head text-lg font-bold tabular-nums">{eur(boat.charged_to_date)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Χρέωση</div>
            <div className="font-head text-lg font-bold tabular-nums">{eur(boat.charged)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Πληρωμές</div>
            <div className="font-head text-lg font-bold tabular-nums text-[#10B981]">{eur(boat.paid)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Υπόλοιπο στη λήξη</div>
            <div className="font-head text-lg font-bold tabular-nums text-foreground">{eur(boat.balance_to_end)}</div>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Charges */}
          <div>
            <h4 className="font-head text-sm font-bold">Λοιπές χρεώσεις</h4>
            <div className="mt-2 flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Υπηρεσία</Label>
                <Select value={charge.description} onValueChange={(v) => {
                  const svc = services.find((s) => s.name === v);
                  let amount = charge.amount;
                  if (svc && svc.price) {
                    const perMeter = /μέτρο|€\s*\/\s*μ/i.test(svc.name);
                    const len = Number(boat.length) || 0;
                    amount = perMeter && len ? String(Number((svc.price * len).toFixed(2))) : String(svc.price);
                  }
                  setCharge({ description: v, amount });
                }}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="charge-desc-select"><SelectValue placeholder="Επιλογή υπηρεσίας" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(services.length ? services : SERVICES.map((s) => ({ name: s, price: 0 }))).map((s) => (
                      <SelectItem key={s.name} value={s.name}>{s.name}{s.price ? ` — ${eur(s.price)}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label className="text-xs text-muted-foreground">Ποσό σε ευρώ</Label>
                <Input type="number" value={charge.amount} onChange={(e) => setCharge({ ...charge, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="charge-amount-input" />
              </div>
              <Button size="icon" className="rounded-sm bg-[#234E7D]" onClick={addCharge} data-testid="add-charge-btn"><Plus size={16} weight="bold" /></Button>
            </div>
            <ul className="mt-3 space-y-1.5" data-testid="charges-list">
              {(boat.charges || []).length === 0 && <li className="text-xs text-muted-foreground">Καμία χρέωση.</li>}
              {(boat.charges || []).map((c) => (
                <li key={c.id} className="flex items-center justify-between rounded-sm border border-border px-3 py-1.5 text-sm">
                  <span>{c.description} <span className="text-xs text-muted-foreground">· {gr(c.date)}</span></span>
                  <span className="flex items-center gap-2 tabular-nums">{eur(c.amount)}
                    <button onClick={() => delCharge(c.id)} className="text-destructive" data-testid={`del-charge-${c.id}`}><Trash size={14} /></button>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Payments */}
          <div>
            <h4 className="font-head text-sm font-bold">Πληρωμές</h4>
            <div className="mt-2 flex items-end gap-2">
              <div className="w-24">
                <Label className="text-xs text-muted-foreground">Ποσό σε ευρώ</Label>
                <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="payment-amount-input" />
              </div>
              <div className="w-40">
                <Label className="text-xs text-muted-foreground">Ημερομηνία πληρωμής</Label>
                <DateField value={pay.date} onChange={(v) => setPay({ ...pay, date: v })} testId="payment-date" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Σημείωση</Label>
                <Input value={pay.note} onChange={(e) => setPay({ ...pay, note: up(e.target.value) })} className="mt-1 rounded-sm" placeholder="προαιρετικό" data-testid="payment-note-input" />
              </div>
              <Button size="icon" className="rounded-sm bg-[#10B981] hover:bg-[#0e9d70]" onClick={addPayment} data-testid="add-payment-btn"><Plus size={16} weight="bold" /></Button>
            </div>
            <ul className="mt-3 space-y-1.5" data-testid="payments-list">
              {(boat.payments || []).length === 0 && <li className="text-xs text-muted-foreground">Καμία πληρωμή.</li>}
              {(boat.payments || []).map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-sm border border-border px-3 py-1.5 text-sm">
                  <span>{p.note || "Πληρωμή"} <span className="text-xs text-muted-foreground">· {gr(p.date)}</span></span>
                  <span className="flex items-center gap-2 tabular-nums text-[#10B981]">{eur(p.amount)}
                    <button onClick={() => delPayment(p.id)} className="text-destructive" data-testid={`del-payment-${p.id}`}><Trash size={14} /></button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
